import {
  getSectionContentById,
  getSectionParagraphsById,
  getSectionPlainTextById,
  getSectionTextAsPlainTextById,
  getSectionTextById,
  getTableByParagraphId,
  getTableIds,
  getTablePlainTextByParagraphId,
  getTextSectionParagraphsById,
} from '../../api/word/word-service'
import type { CurrentDocumentContextEntry } from '../../types'
import type { SectionType } from '../../types/files'
import { normalizeToCanonical } from '../html-sanitization/normalize-to-canonical'

export type WorkingDocSection = {
  id: string
  type: SectionType
}

export type BuildWorkingDocSectionEntriesOptions = {
  perParagraph: boolean
  useHtml: boolean
}

export async function buildWorkingDocSectionEntries(
  section: WorkingDocSection,
  options: BuildWorkingDocSectionEntriesOptions
): Promise<CurrentDocumentContextEntry[]> {
  const { perParagraph, useHtml } = options

  // Per-paragraph output gives paragraph ids for targeted edits or comment anchors.
  // - text: direct body paragraphs under a heading
  // - heading: whole section subtree (heading + sub-headings + body + tables)
  if (perParagraph) {
    const paragraphs: { id: string; text: string }[] = []
    if (section.type === 'text' || section.type === 'heading') {
      paragraphs.push(
        ...(section.type === 'text'
          ? await getTextSectionParagraphsById(section.id)
          : await getSectionParagraphsById(section.id))
      )
    }
    if (section.type === 'table') {
      paragraphs.push(...(await getTableIds(section.id)))
    }
    return paragraphs.map((paragraph) => ({
      type: 'section' as const,
      id: paragraph.id,
      content: paragraph.text,
    }))
  }

  let content: string | null = null

  if (section.type === 'table') {
    if (useHtml) {
      const tableResult = await getTableByParagraphId(section.id)
      if (!tableResult) return []
      content = tableResult.html
    } else {
      content = await getTablePlainTextByParagraphId(section.id)
    }
  } else if (section.type === 'text') {
    if (useHtml) {
      const textResult = await getSectionTextById(section.id)
      if (!textResult) return []
      content = textResult.html
    } else {
      content = await getSectionTextAsPlainTextById(section.id)
    }
  } else if (useHtml) {
    const sectionResult = await getSectionContentById(section.id)
    if (!sectionResult) return []
    content = sectionResult.html
  } else {
    content = await getSectionPlainTextById(section.id)
  }

  if (content === null) return []

  // normalizeToCanonical only applies to getHtml() output; plain-text results have nothing to strip
  return [
    {
      type: 'section' as const,
      id: section.id,
      content: useHtml ? normalizeToCanonical(content) : content,
    },
  ]
}
